/**
 * Permission model for admin actions (Zitadel-style).
 * Format: "resource:action" (e.g. "subscriptions:delete").
 *
 * Mapping conventions:
 * - read   → view modules, lists, details, exports (CSV/PDF/Excel)
 * - write  → create new records
 * - update → edit existing records
 * - delete → delete/cancel/remove (e.g. cancel paid subscription)
 */

export const PERMISSION_RESOURCES = [
  'subscriptions',
  'users',
  'settings',
  'contents',
  'transactions',
  // Módulo de Cartões (status do token do cartão). Declarado no front antes do
  // backend: até o GetUserPermissions devolver `cards:*`, o módulo fica oculto.
  'cards',
  'reports',
] as const;

export type PermissionResource = (typeof PERMISSION_RESOURCES)[number];

export const PERMISSION_ACTIONS = ['read', 'write', 'update', 'delete'] as const;

export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

export type Permission = `${PermissionResource}:${PermissionAction}` | string;

export function buildPermission(
  resource: PermissionResource | string,
  action: PermissionAction | string
): string {
  return `${resource}:${action}`;
}

export function parsePermission(
  permission: string
): { resource: string; action: string } | null {
  if (!permission || !permission.includes(':')) {
    return null;
  }
  const [resource, action] = permission.split(':');
  if (!resource || !action) {
    return null;
  }
  return { resource, action };
}

/** Well-known permissions used by the admin UI. */
export const Permissions = {
  subscriptions: {
    read: 'subscriptions:read',
    write: 'subscriptions:write',
    update: 'subscriptions:update',
    delete: 'subscriptions:delete',
  },
  users: {
    read: 'users:read',
    write: 'users:write',
    update: 'users:update',
    delete: 'users:delete',
  },
  settings: {
    read: 'settings:read',
    write: 'settings:write',
    update: 'settings:update',
    delete: 'settings:delete',
  },
  contents: {
    read: 'contents:read',
    write: 'contents:write',
    update: 'contents:update',
    delete: 'contents:delete',
  },
  transactions: {
    read: 'transactions:read',
    write: 'transactions:write',
    update: 'transactions:update',
    delete: 'transactions:delete',
  },
  cards: {
    read: 'cards:read',
    write: 'cards:write',
    update: 'cards:update',
    delete: 'cards:delete',
  },
  reports: {
    read: 'reports:read',
    write: 'reports:write',
    update: 'reports:update',
    delete: 'reports:delete',
  },
} as const;

/**
 * Maps side-menu / route feature keys to the Zitadel resource used for `:read` access.
 * Todo módulo exige `<resource>:read` — nenhum é herdado de outra permissão.
 *
 * Feature key sem entrada aqui usa a própria chave como resource — ou seja,
 * exige `<featureKey>:read`. Nada é liberado por herança de outro resource.
 */
export const FEATURE_RESOURCE_MAP: Record<string, PermissionResource | 'roles'> = {
  subscriptions: 'subscriptions',
  users: 'users',
  contents: 'contents',
  settings: 'settings',
  transactions: 'transactions',
  cards: 'cards',
  reports: 'reports',
  roles: 'roles',
  // `campaigns`, `activity` e `help` não têm resource próprio no
  // GetUserPermissions e por isso ficam inacessíveis até o backend devolvê-los.
  // Herdar `settings` liberava o módulo para quem só tinha `settings:read`.
};

/**
 * Todas as feature keys navegáveis (side-menu + rotas).
 * Usada para auditoria/diagnóstico das permissões.
 */
export const NAVIGABLE_FEATURE_KEYS = [
  'subscriptions',
  'users',
  'contents',
  'settings',
  'transactions',
  'cards',
  'campaigns',
  'reports',
  'roles',
  'activity',
  'help',
] as const;

export const USER_PERMISSIONS_STORAGE_KEY = 'user_permissions';

export const PERMISSION_DENIED_MESSAGE =
  'Você não tem permissão para realizar esta ação. Solicite acesso ao administrador.';
